import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, signal, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { CatalogService } from '@services/data/catalog.service';
import { Global } from '@services/core/Global';
import { ClaimService } from '../../services/claim.service';
import { formatVehiclePartLabel } from '../../../management-vehicle/vehicle-damage-selector/vehicle-damage-parts';

const PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
const FETCH_CONCURRENCY = 6;
const PDF_RASTER_SCALE = 1.4;
const PDF_JPEG_QUALITY = 0.82;
const EVIDENCE_CACHE_MAX = 40;

interface PdfEvidence {
  title: string;
  dataUrl: string;
  type: 'IMAGE' | 'PDF_PAGE';
  w: number;
  h: number;
  bytes?: Uint8Array;
  format?: string;
}

interface EvidenceBundle {
  payment: PdfEvidence[];
  details: PdfEvidence[][];
  general: PdfEvidence[];
}

let pdfJsLoader: Promise<any> | null = null;
const evidenceCache = new Map<string, PdfEvidence[]>();

export interface ClaimInformePdfContext {
  claim: any;
  party: any;
  reimbursement?: any;
  skipHydrate?: boolean;
  /** En bandeja de reembolsos la primera página es solo el comprobante de transferencia. */
  transferCover?: boolean;
}

@Component({
  selector: 'app-claim-informe-pdf',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './claim-informe-pdf.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./claim-informe-pdf.component.css']
})
export class ClaimInformePdfComponent {
  inline = input<boolean>(false);
  public isGeneratingPdf = signal<boolean>(false);
  public pdfSrcUrl = signal<SafeResourceUrl | null>(null);

  claim = signal<any>(null);
  party = signal<any>(null);
  reimbursement = signal<any>(null);
  transferCover = signal<boolean>(false);

  @ViewChild('pdfPreviewModal') pdfPreviewModal!: TemplateRef<any>;

  private catalogService = inject(CatalogService);
  private claimService = inject(ClaimService);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);
  private cdr = inject(ChangeDetectorRef);
  private paymentPageCount = 0;
  private pdfBlobUrl: string | null = null;

  get details(): any[] {
    return this.claim()?.details ?? [];
  }

  get isTotalLoss(): boolean {
    return this.claim()?.claimType === 'TOTAL_LOSS';
  }

  get currentVehicleValue(): number {
    if (this.claim()?.claimType === 'ACCIDENT_THIRD_PARTY') return 0;
    return Number(this.claim()?.vehicle?.commercialValue ?? 0);
  }

  get totalDeductible(): number {
    const stored = this.details.reduce((acc, d) => acc + Number(d.deductible ?? 0), 0);
    if (stored > 0) return stored;
    if (this.isTotalLoss) {
      return this.currentVehicleValue * (this.totalLossRetentionPct / 100);
    }
    return Number(this.claim()?.deductible ?? 0);
  }

  get totalLossRetentionPct(): number {
    return 15;
  }

  get totalCost(): number {
    if (this.isTotalLoss) {
      return this.details.length > 0 ? this.currentVehicleValue : Number(this.claim()?.totalCost ?? 0);
    }
    return Number(this.claim()?.totalCost ?? this.getTotalDetailsSum());
  }

  get netReimbursement(): number {
    return Math.max(0, this.totalCost - this.totalDeductible);
  }

  async generate(ctx: ClaimInformePdfContext): Promise<void> {
    this.isGeneratingPdf.set(true);
    this.pdfSrcUrl.set(null);
    this._applyContext(ctx);
    this.cdr.detectChanges();
    try {
      const pdfJsWarm = this._initPdfJs().catch(() => null);
      const paymentPromise = this.transferCover()
        ? this._prepareDocs(ctx.reimbursement?.documents ?? [])
        : Promise.resolve([] as PdfEvidence[]);
      await this._hydrateIfNeeded(ctx);
      this.cdr.detectChanges();
      const evidencePromise = this._prepareEvidenceBundle(paymentPromise);
      await Promise.all([new Promise<void>(r => setTimeout(r, 0)), pdfJsWarm]);
      await this.generateClaimPDFForPreview(evidencePromise);
    } catch (err) {
      console.error('Error al generar la vista previa del PDF:', err);
      if (!this.inline()) {
        Swal.fire('Error', 'No se pudo generar el informe PDF.', 'error');
      }
    } finally {
      this.isGeneratingPdf.set(false);
      this.cdr.detectChanges();
    }
  }

  async openPreview(ctx: ClaimInformePdfContext): Promise<void> {
    this.modalService.open(this.pdfPreviewModal, { size: 'xl', centered: true, backdrop: 'static' });
    await this.generate(ctx);
  }

  openGeneratedModal(): void {
    this.modalService.open(this.pdfPreviewModal, { size: 'xl', centered: true, backdrop: 'static' });
  }

  downloadPdf(): void {
    if (!this.pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = this.pdfBlobUrl;
    a.download = this.reimbursement() ? 'Informe_siniestro_reembolso.pdf' : 'Informe_siniestro.pdf';
    a.click();
  }

  findGroup(slug: string): string {
    const party = this.party();
    const named = party?.group?.name ?? party?.groupName;
    if (named && named !== 'Sin Grupo') return named;
    const key = String(slug || party?.groupSlug || party?.groupCode || '').trim();
    const g = this.catalogService.groups()?.find((x: any) =>
      String(x.slug || '').toLowerCase() === key.toLowerCase()
      || String(x.code || '').toLowerCase() === key.toLowerCase()
    );
    return g?.name || named || 'N/A';
  }

  getEntityName(code: number | string): string {
    const party = this.party();
    const req = this.pdfRequest();
    const fromParty = party?.entity;
    const fromReq = req?.entity;
    if (!code) return fromParty?.name || fromReq?.name || 'N/A';
    const entity = this.catalogService.entities().find(e => e.code == code);
    if (entity) return entity.name;
    if (fromParty && (fromParty.code == code || fromParty.name)) {
      return fromParty.name || String(fromParty.code ?? code);
    }
    if (fromReq && (fromReq.code == code || fromReq.name)) {
      return fromReq.name || String(fromReq.code ?? code);
    }
    return fromParty?.name || fromReq?.name || `Entidad ${code}`;
  }

  formatNumber(value: any): string {
    return Number(value ?? 0).toLocaleString('es-CL');
  }

  getTotalDetailsSum(): number {
    if (this.isTotalLoss) {
      return this.details.length > 0 ? this.currentVehicleValue : 0;
    }
    return this.details.reduce((acc: number, d: any) => acc + Number(d.totalCost ?? 0), 0);
  }

  getDriverFormatted(det: any): string {
    const party = this.party();
    const isSocio = det.driverLicense === party?.rut || !det.driverLicense;
    return isSocio ? 'Socio' : `${det.driverName} (RUT: ${det.driverLicense} - Clase: ${det.driverLicenseClass ?? 'N/A'})`;
  }

  getPartsFormatted(det: any): string {
    try {
      const parts = Array.isArray(det.affectedParts) ? det.affectedParts : JSON.parse(det.affectedParts ?? '[]');
      const type = this.claim()?.vehicle?.model?.vehicleType ?? this.claim()?.vehicle?.modelType;
      return parts.map((p: string) => formatVehiclePartLabel(p, type)).join(', ') || 'Ninguna';
    } catch {
      return 'Ninguna';
    }
  }

  getPdfVehiclePlateAndDesc(): string {
    const v = this.claim()?.vehicle;
    if (v) return `${v.plate} - ${v.brand?.name ?? v.brandName} ${v.model?.name ?? v.modelName}`;
    return 'N/A';
  }

  getPdfVehicleDesc(): string {
    const v = this.claim()?.vehicle ?? this.pdfRequest()?.vehicle;
    if (!v) return 'N/A';
    return `${v.brand?.name ?? v.brandName ?? ''} ${v.model?.name ?? v.modelName ?? ''}`.trim() || 'N/A';
  }

  getPdfThirdPartyVehicleDesc(): string {
    const claim = this.claim();
    const brandName = claim?.thirdPartyBrand?.name ?? claim?.thirdPartyBrandName ?? this._lookupBrandName() ?? 'N/A';
    const modelName = claim?.thirdPartyModel?.name ?? claim?.thirdPartyModelName ?? this._lookupModelName() ?? 'N/A';
    return `${brandName} ${modelName} (${this.getPdfFieldValue('thirdPartyYear') ?? claim?.thirdPartyYear ?? 'N/A'})`;
  }

  getPdfFieldValue(field: string): any {
    const claim = this.claim();
    const direct = claim?.[field];
    if (direct !== undefined && direct !== null) return direct;
    const tp = claim?.thirdPartyInfo;
    if (!tp) return undefined;
    if (field === 'thirdPartyPlate') return tp.plate;
    if (field === 'thirdPartyHasInsurance') return tp.hasInsurance;
    if (field === 'thirdPartyTookResponsibility') return tp.tookResponsibility;
    if (field.startsWith('thirdParty')) {
      const key = field.replace('thirdParty', '');
      const normalizedKey = key.charAt(0).toLowerCase() + key.slice(1);
      return tp[field] ?? tp[normalizedKey];
    }
    return undefined;
  }

  getMinistroApprovedBy(): string {
    return this.claim()?.ministroApprovedBy ?? this.getPdfFieldValue('ministroApprovedBy') ?? 'N/A';
  }

  getMinistroApprovalDate(): string {
    const rawDate = this.claim()?.ministroApprovalDate ?? this.getPdfFieldValue('ministroApprovalDate');
    if (!rawDate) return 'N/A';
    const d = new Date(rawDate);
    return isNaN(d.getTime()) ? 'N/A' : `${d.toLocaleDateString('es-CL')} ${d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`;
  }

  getConfirmationToken(): string {
    const req = this.pdfRequest();
    const claim = this.claim();
    return req?.confirmationToken ?? claim?.confirmationToken ?? this.getPdfFieldValue('confirmationToken') ?? 'N/A';
  }

  formatPaidAt(value: any): string {
    return this.formatDate(value);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('es-CL');
  }

  formatDateTime(value: any): string {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return `${d.toLocaleDateString('es-CL')} ${d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`;
  }

  get claimTypeLabel(): string {
    const type = this.getPdfFieldValue('claimType') || this.claim()?.claimType;
    if (type === 'TOTAL_LOSS') return 'Pérdida total';
    if (type === 'ACCIDENT_THIRD_PARTY') return 'Accidente de terceros';
    if (type === 'ACCIDENT') return 'Accidente simple';
    return type || 'Siniestro';
  }

  get claimStatusLabel(): string {
    const req = this.pdfRequest();
    const named = req?.statusObj?.name;
    if (named) return named;
    const slug = req?.statusSlug || this.reimbursement()?.statusSlug;
    return this._statusSlugLabel(slug);
  }

  get claimFolio(): string {
    const uuid = this.claim()?.uuid;
    if (!uuid) return '—';
    return String(uuid).slice(0, 8).toUpperCase();
  }

  get vehicleYear(): string {
    const v = this.claim()?.vehicle ?? this.pdfRequest()?.vehicle;
    return String(v?.year ?? v?.vehicleYear ?? '—');
  }

  get vehicleCommercialValue(): string {
    const v = this.claim()?.vehicle ?? this.pdfRequest()?.vehicle;
    const n = Number(v?.commercialValue ?? 0);
    return n > 0 ? `$${this.formatNumber(n)}` : '—';
  }

  get detailSectionPrefix(): number {
    return this.reimbursement() ? 7 : 6;
  }

  get reimbursementSectionNumber(): number {
    return 6;
  }

  pdfRequest(): any {
    return this.reimbursement()?.request ?? this.claim()?.request ?? null;
  }

  get approvalMilestones(): Array<{ step: string; result: string; actor: string; date: string; note: string }> {
    const req = this.pdfRequest();
    const claim = this.claim();
    const reimb = this.reimbursement();
    const md = req?.ministryDecision;
    const cd = req?.commissionDecision;
    const pd = req?.paymentDecision;
    const status = req?.statusSlug || reimb?.statusSlug || '';
    const rows: Array<{ step: string; result: string; actor: string; date: string; note: string }> = [];

    rows.push({
      step: 'Ingreso',
      result: 'Registrado',
      actor: 'Bandeja FPV',
      date: this.formatDateTime(req?.createdAt || claim?.createdAt),
      note: this.claimTypeLabel
    });

    const ministryRejected = md?.status === 'REJECTED' || (status === 'REJECTED' && !claim?.ministroApprovalDate && md?.decidedAt);
    const ministryDone = !!(claim?.ministroApprovalDate || (md?.decidedAt && md.status !== 'REJECTED'));
    rows.push({
      step: 'Ministro de Fe',
      result: ministryRejected ? 'Rechazado' : (ministryDone ? 'Aprobado' : (status === 'PENDING' ? 'Pendiente' : '—')),
      actor: this._cleanActor(claim?.ministroApprovedBy || md?.actorLabel) || 'Ministro de Fe',
      date: this.formatDateTime(claim?.ministroApprovalDate || md?.decidedAt),
      note: this._channelLabel(md?.channel)
    });

    const voto = String(cd?.gloss || reimb?.request?.commissionDecision?.gloss || '').trim();
    const commissionDone = !!(cd?.decidedAt || voto);
    const showCommission = status !== 'PENDING' || commissionDone;
    if (showCommission) {
      const commissionRejected = cd?.status === 'REJECTED' || (status === 'REJECTED' && !ministryRejected);
      rows.push({
        step: 'Comisión / rol revisor',
        result: commissionRejected ? 'Rechazada' : (commissionDone ? 'Aprobada' : (status === 'MINISTRY_APPROVED' ? 'Pendiente' : '—')),
        actor: this._cleanActor(cd?.actorLabel) || this._roleLabel(cd?.actorRole) || 'Rol revisor',
        date: this.formatDateTime(cd?.decidedAt),
        note: voto ? `Acta / voto: ${voto}` : (this._channelLabel(cd?.channel) || '')
      });
    }

    const paid = ['PAID', 'REIMBURSED'].includes(reimb?.statusSlug) || ['PAID', 'REIMBURSED'].includes(status) || ['PAID', 'REIMBURSED'].includes(pd?.toStatusSlug);
    const showPayment = !!(reimb || pd || ['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT', 'PAID', 'REIMBURSED'].includes(status));
    if (showPayment) {
      const isReimb = status === 'REIMBURSED' || reimb?.statusSlug === 'REIMBURSED' || pd?.toStatusSlug === 'REIMBURSED';
      rows.push({
        step: isReimb ? 'Reembolso' : 'Pago',
        result: paid ? (isReimb ? 'Reembolsado' : 'Pagado') : (isReimb ? 'Pendiente de reembolso' : 'Pendiente de pago'),
        actor: this._cleanActor(pd?.actorLabel) || 'Tesorería',
        date: this.formatDateTime(reimb?.paidAt || pd?.decidedAt),
        note: reimb?.transactionNumber ? `N° transacción ${reimb.transactionNumber}` : (this._channelLabel(pd?.channel) || '')
      });
    }
    return rows;
  }

  getReimbursementStatusLabel(): string {
    const slug = this.reimbursement()?.statusSlug;
    return this._statusSlugLabel(slug);
  }

  getTransferDestinationLabel(): string {
    const item = this.reimbursement();
    if (!item) return 'N/A';
    if (String(item.transferTarget || '').toUpperCase() === 'SOCIO') {
      return `Al socio (${item.party?.names || this.party()?.names || 'socio'})`;
    }
    const campus = item.entity?.campus || item.entity?.name || this.getEntityName(item.entityCode);
    return `Al campus (${campus})`;
  }

  private _cleanActor(label: any): string {
    if (!label) return '';
    return String(label).replace(/\s*\([^)]*@[^)]*\)\s*/g, '').trim();
  }

  private _roleLabel(role: any): string {
    return this.catalogService.getRoleDisplayName(role, '');
  }

  private _channelLabel(channel: any): string {
    const c = String(channel || '').toUpperCase();
    if (c === 'TOKEN') return 'Enlace de firma';
    if (c === 'INBOX') return 'Bandeja';
    if (c === 'SYSTEM') return 'Sistema';
    return channel ? String(channel) : '';
  }

  private _statusSlugLabel(slug: any): string {
    return this.catalogService.getStatusName(slug);
  }

  private _applyContext(ctx: ClaimInformePdfContext): void {
    const claim = ctx.claim ?? null;
    const request = ctx.reimbursement?.request ?? claim?.request ?? null;
    const vehicle = claim?.vehicle ?? request?.vehicle ?? null;
    const details = Array.isArray(claim?.details) ? claim.details : [];
    this.claim.set(claim ? { ...claim, request: claim.request ?? request, vehicle, details } : null);
    const rawParty = ctx.party ?? claim?.party ?? request?.party ?? null;
    const entity = rawParty?.entity ?? request?.entity ?? null;
    this.party.set(rawParty
      ? {
          ...rawParty,
          entity,
          entityCode: rawParty.entityCode ?? entity?.code ?? request?.entityCode ?? null
        }
      : null);
    this.reimbursement.set(ctx.reimbursement ?? null);
    this.transferCover.set(!!ctx.transferCover && !!ctx.reimbursement);
  }

  private async _hydrateIfNeeded(ctx: ClaimInformePdfContext): Promise<void> {
    const current = this.claim();
    if (ctx.skipHydrate) return;
    const key = current?.uuid ?? current?.id;
    if (!key) return;
    if (Array.isArray(current?.documents) && Array.isArray(current?.details)
      && (current.request?.flowHistory || current.request?.ministryDecision)) return;
    try {
      const fullClaim = await firstValueFrom(this.claimService.getClaim(key));
      if (fullClaim) {
        this.claim.set({ ...current, ...fullClaim, details: fullClaim.details ?? current.details });
        if (!this.party() && fullClaim.party) this.party.set(fullClaim.party);
      }
    } catch (reloadErr) {
      console.error('No se pudo recargar el siniestro para el PDF; se usa el payload en memoria.', reloadErr);
    }
  }

  private async generateClaimPDFForPreview(evidencePromise: Promise<EvidenceBundle>): Promise<void> {
    const doc = new jsPDF('p', 'pt', 'letter');
    const pdfWidth = doc.internal.pageSize.getWidth() - 35;
    const entityName = this.getEntityName(this.party()?.entityCode);

    let typeName = 'ACCIDENTE SIMPLE';
    const claimType = this.getPdfFieldValue('claimType') || this.claim()?.claimType;
    if (claimType === 'ACCIDENT_THIRD_PARTY') typeName = 'ACCIDENTE DE TERCEROS';
    else if (claimType === 'TOTAL_LOSS' || this.isTotalLoss) typeName = 'PÉRDIDA TOTAL';
    else if (claimType === 'ACCIDENT') typeName = 'ACCIDENTE SIMPLE';
    if (this.reimbursement()) typeName = `${typeName} Y REEMBOLSO`;

    const bundle = await evidencePromise;
    this.paymentPageCount = 0;
    if (this.transferCover()) {
      const drewCover = this._drawTransferCoverPages(doc, bundle.payment);
      if (drewCover) {
        this.paymentPageCount = (doc as any).internal.getNumberOfPages();
        doc.addPage();
      }
    }
    this.cdr.detectChanges();
    this._drawPDFHeaderTable(doc);
    let currentY = ((doc as any).lastAutoTable?.finalY ?? 150) + 25;
    currentY = this._drawPDFDetailsSection(doc, pdfWidth, entityName, currentY, bundle.details);
    this._drawPDFGeneralEvidences(doc, currentY, pdfWidth, entityName, bundle.general);

    this._stampHeadersAndFooters(doc, entityName, typeName, pdfWidth);
    this._publishPDF(doc);
  }

  private _sectionRow(title: string): any[] {
    return [{
      content: title,
      colSpan: 4,
      styles: {
        fontStyle: 'bold',
        fontSize: 10,
        fillColor: [229, 230, 230],
        textColor: [52, 58, 64],
        halign: 'left'
      }
    }];
  }

  private _labelCell(text: string): any {
    return { content: text, styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } };
  }

  // Trunca una cadena a un maximo de palabras agregando elipsis
  truncateWords(text: string | null | undefined, maxWords = 18): string {
    return this._truncateWords(text, maxWords);
  }

  // Trunca internamente una cadena a un maximo de palabras
  private _truncateWords(text: string | null | undefined, maxWords = 18): string {
    const raw = String(text ?? '').trim();
    if (!raw) return 'N/A';
    const words = raw.split(/\s+/);
    if (words.length <= maxWords) return raw;
    return `${words.slice(0, maxWords).join(' ')}...`;
  }

  // Genera las filas de identificacion del socio y vehiculo
  private _partyVehicleRows(): any[][] {
    const rows = [...this._partyRows(), ...this._vehicleRows()];
    if (this.getPdfFieldValue('claimType') === 'ACCIDENT_THIRD_PARTY') rows.push(...this._thirdPartyRows());
    return rows;
  }

  // Genera las filas correspondientes al socio propietario
  private _partyRows(): any[][] {
    const p = this.party();
    const entityCode = p?.entityCode ?? p?.entity?.code ?? this.pdfRequest()?.entityCode ?? '';
    const entityLabel = [entityCode, this.getEntityName(entityCode)].filter(Boolean).join(' - ') || 'N/A';
    return [
      this._sectionRow('1. SOCIO / PROPIETARIO'),
      [this._labelCell('Nombre'), { content: `${p?.names ?? ''} ${p?.lastNames ?? ''}`.trim() || 'N/A', colSpan: 3 }],
      [this._labelCell('RUT'), p?.rut ?? 'N/A', this._labelCell('Tipo de socio'), this.findGroup(p?.groupSlug)],
      [this._labelCell('Entidad'), { content: entityLabel, colSpan: 3 }],
      [this._labelCell('Correo'), p?.mail || 'No registrado', this._labelCell('Móvil'), p?.mobileNumber || 'No registrado']
    ];
  }

  // Genera las filas de datos del vehiculo
  private _vehicleRows(): any[][] {
    const v = this.claim()?.vehicle ?? this.pdfRequest()?.vehicle;
    return [
      this._sectionRow('2. VEHÍCULO DEL SOCIO'),
      [this._labelCell('Patente'), v?.plate || 'N/A', this._labelCell('Año'), this.vehicleYear],
      [this._labelCell('Marca / modelo'), this.getPdfVehicleDesc(), this._labelCell('Valor comercial'), this.vehicleCommercialValue]
    ];
  }

  // Genera las filas correspondientes al tercero afectado
  private _thirdPartyRows(): any[][] {
    return [
      this._sectionRow('2.1 TERCERO AFECTADO'),
      [this._labelCell('Conductor'), this.getPdfFieldValue('thirdPartyDriverName') ?? '', this._labelCell('RUT / licencia'), `${this.getPdfFieldValue('thirdPartyLicense') ?? ''} (Clase ${this.getPdfFieldValue('thirdPartyLicenseClass') || 'N/A'})`],
      [this._labelCell('Propietario'), this.getPdfFieldValue('thirdPartyOwnerName') ?? '', this._labelCell('Teléfono'), this.getPdfFieldValue('thirdPartyPhone') || 'N/A'],
      [this._labelCell('Patente / vehículo'), `${this.getPdfFieldValue('thirdPartyPlate') ?? ''} — ${this.getPdfThirdPartyVehicleDesc()}`, this._labelCell('Seguro / responsabilidad'), `${this.getPdfFieldValue('thirdPartyHasInsurance') ? 'Con seguro' : 'Sin seguro'} · ${this.getPdfFieldValue('thirdPartyTookResponsibility') ? 'Asumió responsabilidad' : 'No asumió'}`]
    ];
  }

  // Construye las filas del consolidado de daños o presupuesto
  private _consolidatedDamageRows(): any[][] {
    return this.details.length > 0 ? this._damageSummaryRows() : this._budgetSummaryRows();
  }

  // Genera las filas de daños consolidados truncando descripcion a 18 palabras
  private _damageSummaryRows(): any[][] {
    const rows: any[][] = [
      this._sectionRow('3. CONSOLIDADO DE DAÑOS Y MONTOS'),
      [
        { content: 'Fecha', styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } },
        { content: 'Lugar', styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } },
        { content: 'Daño', styles: { fontStyle: 'bold', fillColor: [229, 230, 230] } },
        { content: 'Monto', styles: { fontStyle: 'bold', fillColor: [229, 230, 230], halign: 'right' } }
      ]
    ];
    for (const det of this.details) {
      const amount = (det.totalCost && Number(det.totalCost) > 0) ? det.totalCost : (this.isTotalLoss ? this.currentVehicleValue : 0);
      rows.push([this.formatDate(det.dateOfLoss), det.geographicLocation || 'N/A', this._truncateWords(det.description, 18), { content: `$${this.formatNumber(amount)}`, styles: { halign: 'right' } }]);
    }
    rows.push(...this._damageTotalsRows());
    return rows;
  }

  // Genera las filas de totales, deducibles y neto del consolidado
  private _damageTotalsRows(): any[][] {
    return [
      [{ content: 'Total bruto de daños', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [229, 230, 230], halign: 'right' } }, { content: `$${this.getTotalDetailsSum().toLocaleString('es-CL')}`, styles: { fontStyle: 'bold', fillColor: [229, 230, 230], halign: 'right' } }],
      [{ content: 'Deducible a cargo del socio', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [248, 215, 218], textColor: [132, 32, 41], halign: 'right' } }, { content: `$${this.totalDeductible.toLocaleString('es-CL')}`, styles: { fontStyle: 'bold', fillColor: [248, 215, 218], textColor: [132, 32, 41], halign: 'right' } }],
      [{ content: 'Neto a reembolsar por FPV', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [209, 231, 221], textColor: [15, 81, 50], halign: 'right' } }, { content: `$${this.netReimbursement.toLocaleString('es-CL')}`, styles: { fontStyle: 'bold', fillColor: [209, 231, 221], textColor: [15, 81, 50], halign: 'right' } }]
    ];
  }

  // Genera el resumen presupuestario cuando no hay detalle de daños
  private _budgetSummaryRows(): any[][] {
    const rows: any[][] = [this._sectionRow('3. RESUMEN DE PRESUPUESTO')];
    if (!this.isTotalLoss) {
      rows.push(
        [{ content: 'Ítem', styles: { fontStyle: 'bold', fillColor: [248, 249, 250] } }, { content: 'Mano de obra', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], halign: 'right' } }, { content: 'Repuestos', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], halign: 'right' } }, { content: 'Total presupuestado', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], halign: 'right' } }],
        ['Presupuesto general', { content: `$${this.formatNumber(this.getPdfFieldValue('laborCost'))}`, styles: { halign: 'right' } }, { content: `$${this.formatNumber(this.getPdfFieldValue('partsCost'))}`, styles: { halign: 'right' } }, { content: `$${this.formatNumber(this.getPdfFieldValue('totalCost'))}`, styles: { fontStyle: 'bold', halign: 'right' } }]
      );
    } else {
      rows.push(
        [{ content: 'Ítem', styles: { fontStyle: 'bold', fillColor: [248, 249, 250] } }, { content: 'Total presupuestado', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [248, 249, 250], halign: 'right' } }],
        ['Presupuesto general', { content: `$${this.formatNumber(this.getPdfFieldValue('totalCost'))}`, colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } }]
      );
    }
    return rows;
  }

  // Construye las filas de la historia de aprobacion BPM
  private _approvalHistoryRows(): any[][] {
    const rows: any[][] = [
      this._sectionRow('4. HISTORIA DE APROBACIÓN'),
      [
        { content: 'Paso', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], fontSize: 8 } },
        { content: 'Resultado', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], fontSize: 8 } },
        { content: 'Responsable', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], fontSize: 8 } },
        { content: 'Fecha / nota', styles: { fontStyle: 'bold', fillColor: [248, 249, 250], fontSize: 8 } }
      ]
    ];
    this.approvalMilestones.forEach((row, i) => {
      rows.push([{ content: `${i + 1}. ${row.step}`, styles: { fontStyle: 'bold' } }, row.result, row.actor, [row.date, row.note].filter(Boolean).join(' · ')]);
    });
    return rows;
  }

  // Construye las filas del detalle de transferencia si existe reembolso
  private _paymentDetailsRows(): any[][] {
    const item = this.reimbursement();
    if (!item) return [];
    return [
      this._sectionRow('5. DETALLE DE PAGO / TRANSFERENCIA'),
      [this._labelCell('Estado del pago'), this.getReimbursementStatusLabel(), this._labelCell('Periodo contable'), `${item.year}/${item.period}`],
      [this._labelCell('N° transacción'), item.transactionNumber || '—', this._labelCell('Fecha de pago'), this.formatPaidAt(item.paidAt)],
      [this._labelCell('Destino'), this.getTransferDestinationLabel(), this._labelCell(item.request?.commissionDecision?.gloss ? 'Acta / voto' : ''), item.request?.commissionDecision?.gloss || ''],
      [{ content: 'Monto pagado (neto)', colSpan: 3, styles: { fontStyle: 'bold', fillColor: [209, 231, 221], textColor: [15, 81, 50], halign: 'right' } }, { content: `$${this.formatNumber(item.amount)}`, styles: { fontStyle: 'bold', fillColor: [209, 231, 221], textColor: [15, 81, 50], halign: 'right' } }]
    ];
  }

  // Renderiza las secciones de cabecera, socio, vehiculo, consolidado e historial
  private _drawPDFHeaderTable(doc: jsPDF): void {
    this._drawPartyVehicleTable(doc);
    this._drawConsolidatedDamageTable(doc);
    this._drawApprovalHistoryTable(doc);
    this._drawPaymentDetailsTable(doc);
  }

  // Dibuja la tabla de socio y vehiculo con columnas simetricas balanceadas
  private _drawPartyVehicleTable(doc: jsPDF): void {
    autoTable(doc, {
      startY: 105,
      theme: 'grid',
      body: this._partyVehicleRows(),
      columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 176 }, 2: { cellWidth: 95 }, 3: { cellWidth: 176 } },
      styles: { fontSize: 8.5, cellPadding: 5, textColor: [33, 37, 41], lineColor: [153, 154, 154], valign: 'middle' },
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  // Dibuja la tabla del consolidado de daños con columnas especificas
  private _drawConsolidatedDamageTable(doc: jsPDF): void {
    const startY = ((doc as any).lastAutoTable?.finalY ?? 260) + 12;
    autoTable(doc, {
      startY,
      theme: 'grid',
      body: this._consolidatedDamageRows(),
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 135 }, 2: { cellWidth: 247 }, 3: { cellWidth: 90, halign: 'right' } },
      styles: { fontSize: 8.5, cellPadding: 5, textColor: [33, 37, 41], lineColor: [153, 154, 154], valign: 'middle' },
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  // Dibuja la tabla del historial de aprobacion
  private _drawApprovalHistoryTable(doc: jsPDF): void {
    const startY = ((doc as any).lastAutoTable?.finalY ?? 400) + 12;
    autoTable(doc, {
      startY,
      theme: 'grid',
      body: this._approvalHistoryRows(),
      columnStyles: { 0: { cellWidth: 130 }, 1: { cellWidth: 90 }, 2: { cellWidth: 130 }, 3: { cellWidth: 192 } },
      styles: { fontSize: 8.5, cellPadding: 5, textColor: [33, 37, 41], lineColor: [153, 154, 154], valign: 'middle' },
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  // Dibuja la tabla de pago o transferencia si existe reembolso
  private _drawPaymentDetailsTable(doc: jsPDF): void {
    const rows = this._paymentDetailsRows();
    if (!rows.length) return;
    const startY = ((doc as any).lastAutoTable?.finalY ?? 500) + 12;
    autoTable(doc, {
      startY,
      theme: 'grid',
      body: rows,
      columnStyles: { 0: { cellWidth: 105 }, 1: { cellWidth: 166 }, 2: { cellWidth: 105 }, 3: { cellWidth: 166 } },
      styles: { fontSize: 8.5, cellPadding: 5, textColor: [33, 37, 41], lineColor: [153, 154, 154], valign: 'middle' },
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
  }

  private _detailTableBody(index: number): any[][] {
    const det = this.details[index];
    if (!det) return [];
    const rows: any[][] = [
      this._sectionRow(`${this.detailSectionPrefix}.${index + 1} DAÑO ${index + 1} — ${this.formatDate(det.dateOfLoss)}`),
      [
        this._labelCell('Fecha'),
        this.formatDate(det.dateOfLoss),
        this._labelCell('Lugar'),
        det.geographicLocation || 'N/A'
      ],
      [
        this._labelCell('Conductor'),
        this.getDriverFormatted(det),
        this._labelCell('Negligencia'),
        det.isNegligence ? 'Sí' : 'No'
      ],
      [this._labelCell('Descripción'), { content: det.description || 'N/A', colSpan: 3 }],
      [this._labelCell('Partes afectadas'), { content: this.getPartsFormatted(det), colSpan: 3 }]
    ];
    if (!this.isTotalLoss) {
      rows.push([
        this._labelCell('Mano de obra'),
        { content: `$${this.formatNumber(det.laborCost)}`, styles: { halign: 'right' } },
        this._labelCell('Repuestos'),
        { content: `$${this.formatNumber(det.partsCost)}`, styles: { halign: 'right' } }
      ]);
    }
    rows.push([
      this._labelCell('Deducible de este daño'),
      { content: `$${this.formatNumber(det.deductible)}`, styles: { halign: 'right' } },
      this._labelCell('Total del daño'),
      { content: `$${this.formatNumber(det.totalCost)}`, styles: { fontStyle: 'bold', halign: 'right' } }
    ]);
    return rows;
  }

  private _drawPDFDetailsSection(
    doc: jsPDF,
    pdfWidth: number,
    entityName: string,
    startY: number,
    detailEvidences: PdfEvidence[][]
  ): number {
    let currentY = startY;
    const details = this.details;
    for (let i = 0; i < details.length; i++) {
      currentY = this._drawSinglePDFDetail(doc, i, currentY, pdfWidth, entityName, detailEvidences[i] ?? []);
    }
    return currentY;
  }

  private _drawSinglePDFDetail(
    doc: jsPDF,
    index: number,
    currentY: number,
    pdfWidth: number,
    entityName: string,
    detailEvidences: PdfEvidence[]
  ): number {
    const y = currentY > 520 ? (doc.addPage(), 110) : currentY;
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      body: this._detailTableBody(index),
      styles: { fontSize: 9, cellPadding: 6, textColor: [33, 37, 41], lineColor: [153, 154, 154], valign: 'middle' },
      tableWidth: 'auto',
      margin: { top: 110, bottom: 80, left: 35, right: 35 }
    });
    const finalY = (doc as any).lastAutoTable?.finalY ?? (y + 150);
    return detailEvidences.length > 0
      ? this._drawImagesOnPdf(doc, detailEvidences, finalY + 15, pdfWidth, entityName)
      : finalY;
  }

  private _drawPDFGeneralEvidences(
    doc: jsPDF,
    currentY: number,
    pdfWidth: number,
    entityName: string,
    images: PdfEvidence[]
  ): number {
    if (images.length === 0) return currentY;
    const y = currentY > 520 ? (doc.addPage(), 110) : currentY;
    return this._drawImagesOnPdf(doc, images, y + 25, pdfWidth, entityName);
  }

  private _drawTransferCoverPages(doc: jsPDF, images: PdfEvidence[]): boolean {
    if (images.length === 0) return false;
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 24;
    const maxW = pageW - margin * 2;
    const maxH = pageH - margin * 2;
    let isFirstPage = true;
    for (const img of images) {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;
      const format = img.type === 'PDF_PAGE' ? 'JPEG' : this._imageFormat(img);
      try {
        const fitted = this._fitInBox(img.w, img.h, maxW, maxH);
        const x = margin + (maxW - fitted.w) / 2;
        const y = margin + (maxH - fitted.h) / 2;
        this._addPdfImage(doc, img, format, x, y, fitted.w, fitted.h);
      } catch (e) {
        console.warn('PDF addImage error (comprobante)', e);
        doc.setFontSize(11);
        doc.text(img.title || 'Comprobante de transferencia', 40, 60);
        doc.text('[No se pudo renderizar el archivo de transferencia]', 40, 80);
      }
    }
    return true;
  }

  private _stampHeadersAndFooters(doc: jsPDF, entityName: string, typeName: string, pdfWidth: number) {
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      if (i <= this.paymentPageCount) continue;
      doc.setPage(i);
      this._addPdfHeader(doc, entityName, `INFORME DE SINIESTRO - ${typeName}`);
      this._addFooterPDF(doc, pdfWidth, i, totalPages);
    }
  }

  private _publishPDF(doc: jsPDF) {
    const blob = new Blob([doc.output('arraybuffer') as any], { type: 'application/pdf' });
    if (this.pdfBlobUrl) URL.revokeObjectURL(this.pdfBlobUrl);
    this.pdfBlobUrl = URL.createObjectURL(blob);
    this.pdfSrcUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl));
  }

  private _addPdfHeader(doc: jsPDF, ent: string, title: string = 'SOLICITUD') {
    const rows = [
      [{ content: 'Fondo de Protección Vehicular', styles: { fontStyle: 'normal' as const } }],
      [{ content: ent, styles: { fontStyle: 'italic' as const } }],
      [{ content: '\n' + title, styles: { fontStyle: 'bold' as const, fontSize: 11 } }]
    ];
    autoTable(doc, {
      startY: 35, theme: 'plain', margin: { left: 0, right: 0 }, tableWidth: 'auto',
      body: rows, bodyStyles: { halign: 'center', cellPadding: 0.3, textColor: [0, 0, 0], fontSize: 10 }
    });
  }

  private _addFooterPDF(doc: jsPDF, pdfWidth: number, pageNumber: number, totalPages: number) {
    autoTable(doc, {
      startY: 742,
      body: [['Impreso: ' + this._getDate(), 'Usuario: FPV', 'Sistema: local.uch', 'Página ' + pageNumber + ' de ' + totalPages]],
      theme: 'plain', bodyStyles: { fontSize: 7, cellPadding: 0.3, textColor: [0, 0, 0] }, tableWidth: 'auto',
      didDrawPage: () => { doc.setLineWidth(0.5); doc.line(35, 737, pdfWidth, 737); }
    });
  }

  private _getDate(): string {
    const d = new Date();
    return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES')}`;
  }

  private async _prepareEvidenceBundle(paymentPromise: Promise<PdfEvidence[]>): Promise<EvidenceBundle> {
    const details = this.details;
    const [payment, detailLists, general] = await Promise.all([
      paymentPromise,
      Promise.all(details.map(det => this._prepareDetailEvidences(det))),
      this._prepareDocs(this._uniqueGeneralDocs(this.claim()?.documents ?? []))
    ]);
    return { payment, details: detailLists, general };
  }

  private _uniqueGeneralDocs(docs: any[]): any[] {
    const general = docs.filter((d: any) => !d.claimDetailId && !d.claim_detail_id && !d.claimDetail?.uuid);
    const isVoto = (d: any) => (d.typeSlug || d.type?.slug) === 'VOTO_COMISION';
    const votos = general.filter(isVoto);
    if (votos.length <= 1) return general;
    const latest = votos.reduce((a, b) => (Number(a.id || 0) >= Number(b.id || 0) ? a : b));
    return [...general.filter(d => !isVoto(d)), latest];
  }

  private async _prepareDetailEvidences(det: any): Promise<PdfEvidence[]> {
    if (det.file) return this._processLocalEvidence(det.file);
    if (!det.id && !det.uuid) return [];
    const docs = (this.claim()?.documents ?? []).filter((sd: any) => this._matchesDetail(sd, det.id, det.uuid));
    return this._prepareDocs(docs);
  }

  private async _prepareDocs(docs: any[]): Promise<PdfEvidence[]> {
    if (!docs.length) return [];
    const batches = await this._pool(docs, FETCH_CONCURRENCY, sd => this._prepareServerDoc(sd));
    return batches.flat();
  }

  private async _pool<T, R>(items: T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> {
    const results: R[] = new Array(items.length);
    let cursor = 0;
    const run = async () => {
      while (cursor < items.length) {
        const index = cursor++;
        results[index] = await worker(items[index], index);
      }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => run()));
    return results;
  }

  private _lookupBrandName(): string | null {
    const code = this.getPdfFieldValue('thirdPartyBrandCode');
    if (!code) return null;
    return this.catalogService.brands()?.find(b => b.code === code || b.id === code)?.name ?? null;
  }

  private _lookupModelName(): string | null {
    const code = this.getPdfFieldValue('thirdPartyModelCode');
    if (!code) return null;
    return this.catalogService.models()?.find(m => m.code === code || m.id === code)?.name ?? null;
  }

  private _isImageFile(fileName: string, mimeType: string): boolean {
    if (!fileName && !mimeType) return false;
    const isImgMime = mimeType?.toLowerCase().startsWith('image/');
    const isImgExt = fileName?.toLowerCase().match(/\.(jpeg|jpg|gif|png|webp|bmp|jfif)$/i);
    return !!(isImgMime || isImgExt);
  }

  private async _processLocalEvidence(file: File): Promise<PdfEvidence[]> {
    const title = `Evidencia Local: ${file.name}`;
    if (this._isImageFile(file.name, file.type)) {
      const prepared = await this._blobToEvidence(file, title, 'IMAGE');
      return prepared ? [prepared] : [];
    }
    try {
      const bytes = await this._fileToArrayBuffer(file);
      if (!bytes?.byteLength) return [];
      return await this._convertPdfToImages(bytes, title);
    } catch (err) {
      console.error('Error al procesar PDF local:', err);
      return [];
    }
  }

  private _matchesDetail(d: any, detailId: any, detailUuid?: string): boolean {
    const docDetailId = d.claimDetailId ?? d.claim_detail_id;
    const matchId = docDetailId && detailId && docDetailId == detailId;
    const matchUuid = d.claimDetail?.uuid && detailUuid && d.claimDetail.uuid === detailUuid;
    return matchId || matchUuid;
  }

  private async _prepareServerDoc(sd: any): Promise<PdfEvidence[]> {
    const f = sd.document ?? (sd.fileName ? sd : null);
    if (!f) return [];
    const uuid = f.uuid ?? f.fileUuid;
    if (!uuid) return [];
    const cached = evidenceCache.get(uuid);
    if (cached) return cached;
    const url = `${Global.url}V1/documents/view/${uuid}`;
    const title = sd.type?.name ?? sd.typeSlug ?? f.fileName ?? 'Documento';
    const { buffer, mimeType } = await this._fetchBinary(url);
    if (!buffer.byteLength) return [];
    const mime = f.mimeType || mimeType;
    let prepared: PdfEvidence[] = [];
    if (this._isImageFile(f.fileName, mime)) {
      const item = await this._bytesToEvidence(buffer, mime, title, f.fileName);
      if (item) prepared = [item];
    } else {
      try {
        prepared = await this._convertPdfToImages(buffer, title);
      } catch (err) {
        console.error('Error al descargar o procesar PDF del servidor:', err);
      }
    }
    this._rememberEvidence(uuid, prepared);
    return prepared;
  }

  private _rememberEvidence(uuid: string, items: PdfEvidence[]): void {
    if (!items.length) return;
    evidenceCache.set(uuid, items);
    if (evidenceCache.size <= EVIDENCE_CACHE_MAX) return;
    const first = evidenceCache.keys().next().value;
    if (first) evidenceCache.delete(first);
  }

  private async _bytesToEvidence(buffer: ArrayBuffer, mime: string, title: string, fileName?: string): Promise<PdfEvidence | null> {
    const format = this._mimeToFormat(mime, fileName);
    const size = format === 'JPEG' ? this._jpegSize(buffer) : format === 'PNG' ? this._pngSize(buffer) : { w: 0, h: 0 };
    if (size.w && size.h) {
      return { title, dataUrl: '', type: 'IMAGE', w: size.w, h: size.h, bytes: new Uint8Array(buffer), format };
    }
    return this._blobToEvidence(new Blob([buffer], { type: mime || 'image/jpeg' }), title, 'IMAGE');
  }

  private _mimeToFormat(mime: string, fileName?: string): string {
    const src = `${mime || ''} ${fileName || ''}`.toLowerCase();
    if (src.includes('jpeg') || src.includes('jpg') || src.includes('jfif')) return 'JPEG';
    if (src.includes('png')) return 'PNG';
    if (src.includes('webp')) return 'WEBP';
    return 'JPEG';
  }

  private _jpegSize(buffer: ArrayBuffer): { w: number; h: number } {
    const v = new DataView(buffer);
    if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return { w: 0, h: 0 };
    let offset = 2;
    while (offset + 8 < v.byteLength) {
      if (v.getUint8(offset) !== 0xff) break;
      const marker = v.getUint8(offset + 1);
      const len = v.getUint16(offset + 2);
      if (marker >= 0xc0 && marker <= 0xc3) {
        return { h: v.getUint16(offset + 5), w: v.getUint16(offset + 7) };
      }
      offset += 2 + len;
    }
    return { w: 0, h: 0 };
  }

  private _pngSize(buffer: ArrayBuffer): { w: number; h: number } {
    const v = new DataView(buffer);
    if (v.byteLength < 24 || v.getUint32(0) !== 0x89504e47) return { w: 0, h: 0 };
    return { w: v.getUint32(16), h: v.getUint32(20) };
  }

  private _blobToEvidence(blob: Blob, title: string, type: PdfEvidence['type']): Promise<PdfEvidence | null> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        if (!dataUrl) {
          resolve(null);
          return;
        }
        const image = new Image();
        image.onload = () => resolve({
          title,
          dataUrl,
          type,
          w: image.naturalWidth || image.width,
          h: image.naturalHeight || image.height
        });
        image.onerror = () => resolve({ title, dataUrl, type, w: 0, h: 0 });
        image.src = dataUrl;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  }

  private async _fetchBinary(url: string): Promise<{ buffer: ArrayBuffer; mimeType: string }> {
    try {
      const res = await fetch(url, { credentials: 'omit', mode: 'cors', cache: 'force-cache' });
      if (!res.ok) return { buffer: new ArrayBuffer(0), mimeType: '' };
      return { buffer: await res.arrayBuffer(), mimeType: res.headers.get('content-type') || '' };
    } catch {
      return { buffer: new ArrayBuffer(0), mimeType: '' };
    }
  }

  private _fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => resolve(new ArrayBuffer(0));
      reader.readAsArrayBuffer(file);
    });
  }

  private _loadScript(url: string): Promise<void> {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
    if (existing) {
      if ((window as any)['pdfjs-dist/build/pdf']) return Promise.resolve();
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('pdf.js load failed')), { once: true });
      });
    }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('pdf.js load failed'));
      document.body.appendChild(s);
    });
  }

  private _initPdfJs(): Promise<any> {
    const w = window as any;
    if (w['pdfjs-dist/build/pdf']) {
      w['pdfjs-dist/build/pdf'].GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      return Promise.resolve(w['pdfjs-dist/build/pdf']);
    }
    if (!pdfJsLoader) {
      pdfJsLoader = this._loadScript(PDFJS_SRC).then(() => {
        const pdfjs = (window as any)['pdfjs-dist/build/pdf'];
        pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        return pdfjs;
      });
    }
    return pdfJsLoader;
  }

  private async _convertPdfToImages(bytes: ArrayBuffer, title: string): Promise<PdfEvidence[]> {
    const pdfjs = await this._initPdfJs();
    const pdf = await pdfjs.getDocument({ data: bytes, disableStream: true, disableAutoFetch: true }).promise;
    const canvas = document.createElement('canvas');
    const list: PdfEvidence[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      list.push(await this._renderPageToEvidence(page, canvas, `${title} - Pág. ${i}`));
    }
    return list;
  }

  private async _renderPageToEvidence(page: any, canvas: HTMLCanvasElement, title: string): Promise<PdfEvidence> {
    const view = page.getViewport({ scale: PDF_RASTER_SCALE });
    canvas.width = view.width;
    canvas.height = view.height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    await page.render({ canvasContext: ctx, viewport: view }).promise;
    return {
      title,
      dataUrl: canvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
      type: 'PDF_PAGE',
      w: canvas.width,
      h: canvas.height
    };
  }

  private _drawImagesOnPdf(doc: jsPDF, images: PdfEvidence[], startY: number, pdfWidth: number, ent: string): number {
    let y = startY;
    for (const img of images) {
      if (img.type === 'PDF_PAGE') {
        doc.addPage();
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(img.title, 35, 120);
        try {
          const fitted = this._fitInBox(img.w, img.h, 542, 585);
          const x = 35 + (542 - fitted.w) / 2;
          this._addPdfImage(doc, img, 'JPEG', x, 135, fitted.w, fitted.h);
        } catch {
          doc.text('[Error al renderizar página de PDF]', 55, 135);
        }
        y = 700;
      } else {
        y = this._drawImageItem(doc, img, y, pdfWidth, ent);
      }
    }
    return y;
  }

  private _imageFormat(img: PdfEvidence): string {
    if (img.format) return img.format;
    return this._getImageFormat(img.dataUrl);
  }

  private _addPdfImage(doc: jsPDF, img: PdfEvidence, format: string, x: number, y: number, w: number, h: number): void {
    const data = img.bytes ?? img.dataUrl;
    doc.addImage(data, format, x, y, w, h);
  }

  private _getImageFormat(dataUrl: string): string {
    if (dataUrl?.startsWith('data:image/webp') || dataUrl?.includes('.webp')) return 'WEBP';
    if (dataUrl?.startsWith('data:image/jpeg') || dataUrl?.startsWith('data:image/jpg')) return 'JPEG';
    return 'PNG';
  }

  private _fitInBox(srcW: number, srcH: number, maxW: number, maxH: number): { w: number; h: number } {
    if (!srcW || !srcH) return { w: maxW, h: maxH };
    const scale = Math.min(maxW / srcW, maxH / srcH);
    return { w: srcW * scale, h: srcH * scale };
  }

  private _drawImageItem(doc: jsPDF, img: PdfEvidence, y: number, pdfWidth: number, _ent: string): number {
    const cy = this._checkImagePageBreak(doc, y);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(img.title, 35, cy);
    const format = this._imageFormat(img);
    const maxW = pdfWidth - 40;
    const maxH = 280;
    try {
      const fitted = this._fitInBox(img.w, img.h, maxW, maxH);
      const x = 55 + (maxW - fitted.w) / 2;
      this._addPdfImage(doc, img, format, x, cy + 10, fitted.w, fitted.h);
      return cy + 20 + fitted.h;
    } catch (e) {
      console.warn('PDF addImage error', e);
      doc.text('[Error al renderizar imagen]', 55, cy + 20);
      return cy + 40;
    }
  }

  private _checkImagePageBreak(doc: jsPDF, y: number): number {
    if (y <= 500) return y;
    doc.addPage();
    return 100;
  }
}
